import React, { useCallback, useEffect, useMemo, useState } from "react";
import FormModal from "./FormModal";
import { getFloorWithRelations } from "../../services/floorService";
import {
  createTable,
  deleteTable,
  addTableJob,
  deleteTableJob,
  getTableJobs,
} from "../../services/tableService";
import { getUnassignedJobsByCompany } from "../../services/jobService";
import { toast } from "../../utils/toast";
import {
  acceptsDesks,
  CELL_ROLES,
  DEFAULT_CELL_TYPE,
  isAbsorbed,
  planCellKey,
  roleOf,
  toPlanCells,
} from "../../utils/floorPlan";
import {
  capacityOfType,
  iconSrcOfType,
  isHorizontalType,
  MAX_TABLES_PER_CELL,
  nameOfType,
  orientationOfType,
  TABLE_TYPES,
  typeIdOfTable,
} from "../../utils/tableTypes";
import "../../styles/floor-layout-designer.css";

const CELL_PX = 60;
const ROW_PX = 68;
const GAP_PX = 4;

/**
 * Glyphs for the cell roles that cannot take a desk. Drawn with `currentColor`
 * so a role's colour is set once in CSS and the icon follows the label.
 *
 * Module scope on purpose: declared inside the component these would be a new
 * component type on every render, and React would rebuild every icon in the
 * grid each time.
 */
const ROLE_GLYPHS = {
  STAIRS: <path d="M4 18h4v-2h4v-2h4v-2h4" />,
  ELEVATOR: (
    <>
      <rect x="6" y="5" width="12" height="14" rx="2" />
      <path d="M10 9l2-2 2 2M10 15l2 2 2-2" />
    </>
  ),
  CORRIDOR: (
    <>
      <path d="M7 3v18M17 3v18" />
      <path d="M12 5v3M12 10.5v3M12 16v3" />
    </>
  ),
  LOBBY: (
    <>
      <path d="M4 13v5h16v-5" />
      <path d="M6 13V9a2 2 0 012-2h8a2 2 0 012 2v4" />
    </>
  ),
  ENTRANCE: (
    <>
      <path d="M14 4h5v16h-5" />
      <path d="M4 12h8M9 9l3 3-3 3" />
    </>
  ),
  VOID: (
    <>
      <path d="M5 5h14v14H5z" strokeDasharray="3 3" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </>
  ),
};

const CellRoleIcon = ({ role, size = 18 }) => {
  const glyph = ROLE_GLYPHS[role];
  if (!glyph) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {glyph}
    </svg>
  );
};

/** Table ids for a cell, tolerating the scalar form older state could hold. */
const idsOf = (value) => {
  if (Array.isArray(value)) return value.slice();
  return value != null ? [value] : [];
};

/**
 * The desks on one floor and the jobs sitting at them.
 *
 * Drops onto the grid create REAL `table` rows immediately — there is no local
 * draft to save — so the grid mirrors the plan the layout designer produced:
 * merged rooms span their rectangle, and only a room takes a desk.
 *
 * @param {number} floorId
 * @param {"edit"|"view"} mode
 * @param {number} [refreshToken] Bump to re-read the floor, e.g. after the
 *   layout designer above has changed cell types or merges.
 * @param {() => void} [onDesksChange] Called once a desk has been created or
 *   deleted. The designer holds the same rooms, and reads their tables for the
 *   seat cap and the room census, so it has to be told they moved.
 */
export default function FloorDeskLayout({
  floorId,
  mode = "edit",
  refreshToken = 0,
  onDesksChange,
}) {
  const notifyDesksChanged = useCallback(() => {
    if (typeof onDesksChange === "function") onDesksChange();
  }, [onDesksChange]);

  const readOnly = mode === "view";

  const [floor, setFloor] = useState(null);
  const [loading, setLoading] = useState(true);

  // Grid state, keyed by `${row}-${col}`.
  const [assignments, setAssignments] = useState({}); // -> first typeId in the cell
  const [cellTables, setCellTables] = useState({}); // -> table count
  const [cellJobs, setCellJobs] = useState({}); // -> per-table array of job ids
  const [cellTableIds, setCellTableIds] = useState({}); // -> per-table table id
  const [cellTableTypes, setCellTableTypes] = useState({}); // -> per-table typeId

  const [jobModal, setJobModal] = useState({ open: false, key: null, index: null });
  const [jobInputs, setJobInputs] = useState([]);
  const [modalError, setModalError] = useState("");
  const [companyJobs, setCompanyJobs] = useState([]);
  const [jobLabelById, setJobLabelById] = useState({});
  const [slotLocked, setSlotLocked] = useState([]);
  const [confirmRemove, setConfirmRemove] = useState({
    open: false,
    key: null,
    index: null,
  });

  const rooms = useMemo(() => floor?.rooms ?? [], [floor]);
  const planCells = useMemo(() => toPlanCells(rooms), [rooms]);

  const building = floor?.building ?? null;
  const gridRows = Number(building?.rows) || 0;
  const gridCols = Number(building?.columns) || 0;
  const stairsCell = building?.stairs_cell ?? null;
  const elevatorCell = building?.elevator_cell ?? null;

  const applyRooms = useCallback((list) => {
    const nextAssignments = {};
    const nextCellTables = {};
    const nextCellTableIds = {};
    const nextCellTableTypes = {};

    (Array.isArray(list) ? list : []).forEach((room) => {
      const key = planCellKey(room.row, room.column);
      const tables = Array.isArray(room.tables)
        ? room.tables.slice(0, MAX_TABLES_PER_CELL)
        : [];
      if (tables.length === 0) return;

      const types = tables.map(typeIdOfTable);
      nextAssignments[key] = types[0];
      nextCellTables[key] = tables.length;
      nextCellTableIds[key] = tables
        .map((t) => t.table_id ?? t.id)
        .filter((id) => id != null);
      nextCellTableTypes[key] = types;
    });

    setAssignments(nextAssignments);
    setCellTables(nextCellTables);
    setCellTableIds(nextCellTableIds);
    setCellTableTypes(nextCellTableTypes);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (floorId == null) {
        if (mounted) {
          setFloor(null);
          setLoading(false);
        }
        return;
      }
      try {
        setLoading(true);
        const data = await getFloorWithRelations(floorId);
        if (!mounted) return;
        setFloor(data ?? null);
        applyRooms(data?.rooms);
      } catch (_) {
        if (mounted) setFloor(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [floorId, refreshToken, applyRooms]);

  // Jobs for the building's company, only while the link modal is open.
  useEffect(() => {
    let mounted = true;
    (async () => {
      const companyId = Number(building?.company_id);
      if (!jobModal.open || !Number.isFinite(companyId) || companyId <= 0) {
        if (mounted) setCompanyJobs([]);
        return;
      }
      try {
        const list = await getUnassignedJobsByCompany(companyId);
        if (mounted) setCompanyJobs(Array.isArray(list) ? list : []);
      } catch (_) {
        if (mounted) setCompanyJobs([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [building?.company_id, jobModal.open]);

  const roomNames = useMemo(() => {
    const label = floor?.name || floor?.floor_code || "Floor";
    const buildingName = building?.name || "Building";
    return rooms
      .filter((room) => !isAbsorbed(planCells.get(planCellKey(room.row, room.column))))
      .map(
        (room) =>
          room.name || `${buildingName} • ${label} • ${room.row}-${room.column}`,
      );
  }, [rooms, planCells, floor, building]);

  const functionNames = useMemo(() => {
    const names = new Set();
    rooms.forEach((room) => {
      (Array.isArray(room?.tables) ? room.tables : []).forEach((table) => {
        const tableJobs =
          (Array.isArray(table?.tableJobs) && table.tableJobs) ||
          (Array.isArray(table?.table_job) && table.table_job) ||
          [];
        tableJobs.forEach((tj) => {
          const fn = tj?.job?.Function || tj?.Function || tj?.job?.function || null;
          const label = fn?.name || fn?.functionName || fn?.functionCode || null;
          if (label) names.add(String(label));
        });
      });
    });
    return Array.from(names);
  }, [rooms]);

  const dropCellState = (key) => {
    setAssignments((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    [setCellTables, setCellJobs, setCellTableIds, setCellTableTypes].forEach(
      (setter) =>
        setter((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        }),
    );
  };

  const removeTableAt = async (key, index) => {
    setModalError("");
    const ids = idsOf(cellTableIds[key]);
    if (index < 0 || index >= ids.length) return;

    const id = ids[index];
    if (id != null) {
      try {
        await deleteTable(id);
        toast.success("Table has been deleted successfully!");
      } catch (_) {
        setModalError("Failed to delete table.");
        toast.error("Failed to delete table.");
        return;
      }
    }

    ids.splice(index, 1);
    notifyDesksChanged();
    if (ids.length === 0) {
      dropCellState(key);
      return;
    }

    const dropIndex = (arr) => arr.filter((_, i) => i !== index);
    setCellTableIds((prev) => ({ ...prev, [key]: ids }));
    setCellTables((prev) => ({ ...prev, [key]: ids.length }));
    setJobInputs((arr) => dropIndex(arr));
    setCellJobs((prev) => ({
      ...prev,
      [key]: dropIndex(Array.isArray(prev[key]) ? prev[key] : []),
    }));
    setCellTableTypes((prev) => ({
      ...prev,
      [key]: dropIndex(Array.isArray(prev[key]) ? prev[key] : []),
    }));
  };

  const onDragStartType = (e, typeId) => {
    e.dataTransfer.setData("application/x-table-type", String(typeId));
    e.dataTransfer.effectAllowed = "copyMove";
  };

  const onDragOverCell = (e) => {
    if (e.dataTransfer.types.includes("application/x-table-type")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const onDropCell = async (e, key) => {
    const typeId = e.dataTransfer.getData("application/x-table-type");
    if (!typeId) return;

    const existingIds = idsOf(cellTableIds[key]);
    const existingCount = existingIds.length;

    if (existingCount >= MAX_TABLES_PER_CELL) {
      toast.error(`A cell can have at most ${MAX_TABLES_PER_CELL} tables.`);
      return;
    }

    const cell = planCells.get(key);
    if (!acceptsDesks(cell)) {
      toast.error(`Cannot place a table on a ${roleOf(cell).label} cell.`);
      return;
    }

    const droppingIsHorizontal = isHorizontalType(typeId);
    if (existingCount > 0) {
      const firstType =
        (Array.isArray(cellTableTypes[key]) && cellTableTypes[key][0]) ||
        assignments[key];
      if (isHorizontalType(firstType) !== droppingIsHorizontal) {
        toast.error("All tables in a cell must have the same orientation.");
        return;
      }
    }

    const roomId = cell?.roomId;
    if (!roomId) {
      toast.error("No room found for this cell");
      return;
    }

    // Optimistic: show the desk, then create it.
    setAssignments((prev) => ({ ...prev, [key]: prev[key] ?? typeId }));
    setCellTables((prev) => ({ ...prev, [key]: existingCount + 1 }));
    setCellJobs((prev) => {
      const next = (Array.isArray(prev[key]) ? prev[key] : []).slice(
        0,
        existingCount + 1,
      );
      while (next.length < existingCount + 1) next.push("");
      return { ...prev, [key]: next };
    });
    setCellTableTypes((prev) => ({
      ...prev,
      [key]: [
        ...(Array.isArray(prev[key]) ? prev[key] : []),
        String(typeId),
      ].slice(0, existingCount + 1),
    }));

    try {
      const capacity = capacityOfType(typeId);
      const letter = droppingIsHorizontal ? "H" : "V";
      const created = await createTable({
        tableCode: `T-${capacity}-${letter}-${roomId}-${existingCount + 1}`,
        name: `Table-${capacity} Person`,
        room_id: roomId,
        capacity,
        orientation: orientationOfType(typeId),
      });
      toast.success(`Table "Table-${capacity} Person" has been created successfully!`);

      const createdId = created?.id ?? created?.table_id;
      if (createdId != null) {
        setCellTableIds((prev) => ({
          ...prev,
          [key]: [...idsOf(prev[key]), createdId],
        }));
      }
      notifyDesksChanged();
    } catch (_) {
      // Roll back exactly the one optimistic addition. Every setter reads its
      // own `prev` — reading the render-time value here is what previously
      // wiped a cell's first table when its second failed to save.
      const pop = (arr) => arr.slice(0, -1);
      setCellTableIds((prev) => ({ ...prev, [key]: pop(idsOf(prev[key])) }));
      setCellJobs((prev) => ({
        ...prev,
        [key]: pop(Array.isArray(prev[key]) ? prev[key] : []),
      }));
      setCellTableTypes((prev) => ({
        ...prev,
        [key]: pop(Array.isArray(prev[key]) ? prev[key] : []),
      }));
      setCellTables((prev) => ({
        ...prev,
        [key]: Math.max(0, (Number(prev[key]) || 0) - 1),
      }));
      if (existingCount === 0) dropCellState(key);
      toast.error("Failed to create table for this room");
    }
  };

  const openJobsForTable = async (key, index) => {
    const idx = Number(index) || 0;
    const typeForTable =
      (Array.isArray(cellTableTypes[key]) && cellTableTypes[key][idx]) ||
      assignments[key];
    const capacity = capacityOfType(typeForTable);

    const existingForTable = (Array.isArray(cellJobs[key]) ? cellJobs[key] : [])[idx];
    let values = Array.isArray(existingForTable)
      ? existingForTable.slice(0, capacity)
      : [];
    while (values.length < capacity) values.push("");

    try {
      const tableId = idsOf(cellTableIds[key])[idx];
      if (tableId) {
        const server = await getTableJobs(tableId);
        if (Array.isArray(server) && server.length > 0) {
          const labels = {};
          server.forEach((r) => {
            const id = Number(r?.job_id ?? r?.job?.job_id ?? r?.job?.id ?? 0);
            const label = String(r?.job?.name || r?.name || "").trim();
            if (id && label) labels[id] = label;
          });
          if (Object.keys(labels).length > 0) {
            setJobLabelById((prev) => ({ ...prev, ...labels }));
          }
          values = server
            .map((r) => Number(r?.job_id))
            .filter(Boolean)
            .slice(0, capacity);
          while (values.length < capacity) values.push("");
        }
      }
    } catch (_) {
      /* keep the local values */
    }

    setJobInputs(values);
    setModalError("");
    setSlotLocked(values.map((v) => Boolean(v)));
    setJobModal({ open: true, key, index: idx });
  };

  const closeJobsModal = () => {
    setJobModal({ open: false, key: null, index: null });
    setJobInputs([]);
    setModalError("");
  };

  const closeConfirmRemove = () =>
    setConfirmRemove({ open: false, key: null, index: null });

  const confirmRemoveTable = async () => {
    const { key, index } = confirmRemove;
    if (key == null || !Number.isFinite(Number(index))) {
      closeConfirmRemove();
      return;
    }
    await removeTableAt(key, Number(index));
    closeConfirmRemove();
    closeJobsModal();
  };

  /** The table this modal is pointed at, or null. */
  const modalTableId = () => {
    const idx = Number(jobModal.index);
    if (!jobModal.key || !Number.isFinite(idx)) return null;
    return idsOf(cellTableIds[jobModal.key])[idx] ?? null;
  };

  const writeSlot = (slotIndex, jobId) => {
    const key = jobModal.key;
    const idx = Number(jobModal.index);
    setCellJobs((prev) => {
      const tables = Array.isArray(prev[key]) ? prev[key].slice() : [];
      while (tables.length <= idx) tables.push([]);
      const current = Array.isArray(tables[idx]) ? tables[idx].slice() : [];
      current[slotIndex] = jobId;
      tables[idx] = current;
      return { ...prev, [key]: tables };
    });
    setSlotLocked((arr) => arr.map((v, i) => (i === slotIndex ? Boolean(jobId) : v)));
    setJobInputs((arr) => arr.map((v, i) => (i === slotIndex ? jobId || "" : v)));
  };

  const onSelectJobAt = async (slotIndex, jobIdStr) => {
    const jobId = Number(jobIdStr) || null;
    setModalError("");
    setJobInputs((arr) => arr.map((v, i) => (i === slotIndex ? jobId || "" : v)));
    if (!jobId) return;

    const tableId = modalTableId();
    if (!tableId) {
      setModalError("Missing table id for this cell");
      return;
    }
    try {
      await addTableJob(tableId, jobId);
      writeSlot(slotIndex, jobId);
      toast.success("Job assigned successfully!");
      await openJobsForTable(jobModal.key, Number(jobModal.index));
    } catch (_) {
      setModalError("Failed to assign job.");
      toast.error("Failed to assign job.");
    }
  };

  /** Unlink the job in a slot. `message` differs for Change vs Remove. */
  const unlinkSlot = async (slotIndex, message) => {
    const currentJobId = jobInputs[slotIndex] || null;
    const tableId = modalTableId();
    if (!tableId || !currentJobId) {
      writeSlot(slotIndex, "");
      return;
    }
    try {
      setModalError("");
      await deleteTableJob(tableId, currentJobId);
      writeSlot(slotIndex, "");
      toast.success(message);
      await openJobsForTable(jobModal.key, Number(jobModal.index));
    } catch (_) {
      setModalError("Failed to remove job from this table.");
      toast.error("Failed to remove job from this table.");
    }
  };

  if (loading) return <div className="no-results">Loading desk layout…</div>;

  return (
    <>
      <div className="fdg-head">
        <div className="fdg-head-title">Desk layout</div>
        <div className="fdg-head-sub">
          {readOnly
            ? "The desks on this floor and the jobs sitting at them."
            : "Drag a desk from the palette into a room. Corridors, lobbies, voids and the cores do not take desks."}
        </div>
      </div>

      {!readOnly && (
        <div className="fdg-palette">
          {TABLE_TYPES.map((type) => (
            <div
              key={type.id}
              draggable
              onDragStart={(e) => onDragStartType(e, type.id)}
              title={`Drag ${type.name}`}
              aria-label={type.name}
              className="fdg-palette-item"
            >
              <img src={iconSrcOfType(type.id)} alt={type.name} width={20} height={20} />
            </div>
          ))}
        </div>
      )}

      <div className="fdg-canvas">
        {gridRows === 0 || gridCols === 0 ? (
          <div className="fdg-empty">
            This floor's building has no rows/columns set, so there is no grid to
            lay desks on.
          </div>
        ) : (
          <div
            className="fdg-grid"
            style={{
              gridTemplateColumns: `repeat(${gridCols}, minmax(${CELL_PX}px, 1fr))`,
              gridTemplateRows: `repeat(${gridRows}, ${ROW_PX}px)`,
              gap: GAP_PX,
              minWidth: gridCols * CELL_PX + (gridCols - 1) * GAP_PX + 16,
            }}
          >
            {Array.from({ length: gridRows }).map((_, rIdx) =>
              Array.from({ length: gridCols }).map((_, cIdx) => {
                const row = rIdx + 1;
                const col = cIdx + 1;
                const key = planCellKey(row, col);
                const cell = planCells.get(key);

                // A cell swallowed by a merge is not drawn — its anchor spans
                // over this position instead.
                if (isAbsorbed(cell)) return null;

                const linear = (row - 1) * gridCols + col;
                const ct = cell?.cellType || "";
                const isStairs = ct
                  ? ct === "STAIRS"
                  : stairsCell != null && Number(stairsCell) === linear;
                const isElevator = ct
                  ? ct === "ELEVATOR"
                  : elevatorCell != null && Number(elevatorCell) === linear;
                const droppable = acceptsDesks(cell) && !isStairs && !isElevator;
                const markerRole = isStairs
                  ? "STAIRS"
                  : isElevator
                    ? "ELEVATOR"
                    : ct || DEFAULT_CELL_TYPE;
                const rowSpan = cell?.rowSpan ?? 1;
                const colSpan = cell?.colSpan ?? 1;
                const tablesInCell = Math.min(
                  Number(cellTables[key]) || idsOf(cellTableIds[key]).length || 0,
                  MAX_TABLES_PER_CELL,
                );

                return (
                  <div
                    key={key}
                    className={`fdg-cell fdg-cell--${(markerRole || "EMPTY").toLowerCase()}${
                      rowSpan > 1 || colSpan > 1 ? " fdg-cell--merged" : ""
                    }`}
                    onDragOver={!readOnly && droppable ? onDragOverCell : undefined}
                    onDrop={
                      !readOnly && droppable ? (e) => onDropCell(e, key) : undefined
                    }
                    style={{
                      // Placed explicitly rather than left to auto-flow: a
                      // merged room must land on its own coordinates.
                      gridColumn: `${col} / span ${colSpan}`,
                      gridRow: `${row} / span ${rowSpan}`,
                    }}
                  >
                    <div className="fdg-cell-coord" aria-hidden="true">
                      {row}-{col}
                      {(rowSpan > 1 || colSpan > 1) && (
                        <span className="fdg-cell-span">
                          {" "}
                          {rowSpan}×{colSpan}
                        </span>
                      )}
                    </div>

                    {!droppable ? (
                      <span className="fdg-cell-role">
                        <CellRoleIcon role={markerRole} />
                        {CELL_ROLES[markerRole]?.label ?? roleOf(cell).label}
                      </span>
                    ) : tablesInCell > 0 ? (
                      <div className="fdg-cell-tables">
                        {Array.from({ length: tablesInCell }).map((_, i) => {
                          const typeId =
                            (Array.isArray(cellTableTypes[key]) &&
                              cellTableTypes[key][i]) ||
                            assignments[key];
                          return (
                            <div
                              key={i}
                              className="fdg-table"
                              onClick={() => openJobsForTable(key, i)}
                              title="Link jobs"
                            >
                              <img
                                src={iconSrcOfType(typeId)}
                                alt={nameOfType(typeId)}
                                width={24}
                                height={24}
                              />
                              {!readOnly && (
                                <button
                                  type="button"
                                  className="fdg-table-remove"
                                  title={`Remove table ${i + 1}`}
                                  aria-label={`Remove table ${i + 1}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeTableAt(key, i);
                                  }}
                                >
                                  <svg width={10} height={10} viewBox="0 0 24 24" fill="none">
                                    <path
                                      d="M6 6l12 12M18 6l-12 12"
                                      stroke="#475569"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="fdg-cell-dot">•</span>
                    )}
                  </div>
                );
              }),
            )}
          </div>
        )}
      </div>

      <div className="fdg-lists">
        <div>
          <div className="fdg-list-label">Rooms</div>
          <div className="fdg-list">
            {roomNames.length === 0 ? (
              <div className="fdg-list-empty">No rooms</div>
            ) : (
              <ul>
                {roomNames.map((name, idx) => (
                  <li key={`${name}-${idx}`}>{name}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div>
          <div className="fdg-list-label">Functions / Subfunctions</div>
          <div className="fdg-list">
            {functionNames.length === 0 ? (
              <div className="fdg-list-empty">No functions assigned</div>
            ) : (
              <ul>
                {functionNames.map((name, idx) => (
                  <li key={`${name}-${idx}`}>{name}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <FormModal
        open={Boolean(jobModal.open)}
        size="large"
        title={
          jobModal.key != null && Number.isFinite(Number(jobModal.index))
            ? `Cell ${jobModal.key} • Table ${Number(jobModal.index) + 1} — Link Job`
            : "Link Job"
        }
        onCancel={closeJobsModal}
        footer={
          <>
            {!readOnly && (
              <button
                type="button"
                className="danger-btn"
                onClick={() =>
                  setConfirmRemove({
                    open: true,
                    key: jobModal.key,
                    index: Number(jobModal.index),
                  })
                }
              >
                Remove this table
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button type="button" className="secondary-btn" onClick={closeJobsModal}>
              Close
            </button>
          </>
        }
      >
        <div className="fdg-modal-hint">
          {readOnly
            ? "The jobs sitting at this table."
            : "One job per seat. Each selection is saved immediately and locks; use Change to swap it."}
        </div>
        {modalError ? <div className="fdg-modal-error">{modalError}</div> : null}

        {(jobInputs || []).length === 0 ? (
          <div className="no-results">No table selected.</div>
        ) : (
          <div className="fdg-slots">
            {jobInputs.map((val, idx) => (
              <div key={idx} className="fdg-slot">
                <div className="fdg-slot-label">Job {idx + 1}</div>
                <div>
                  <select
                    value={val || ""}
                    onChange={(e) => onSelectJobAt(idx, e.target.value)}
                    disabled={readOnly || Boolean(slotLocked[idx])}
                  >
                    <option value="">Select a job…</option>
                    {companyJobs.map((j) => (
                      <option key={j.id ?? j.label} value={j.id}>
                        {j.label}
                      </option>
                    ))}
                    {/* The list only holds UNASSIGNED jobs, so the one already
                        sitting here has to be re-added or the select would show
                        blank for a filled seat. */}
                    {slotLocked[idx] &&
                      Number(val || 0) > 0 &&
                      !companyJobs.some((j) => Number(j.id) === Number(val)) && (
                        <option value={Number(val)}>
                          {jobLabelById[Number(val)] || `Job ${Number(val)}`}
                        </option>
                      )}
                  </select>
                  {companyJobs.length === 0 ? (
                    <div className="fdg-slot-note">
                      No unassigned jobs found for this company.
                    </div>
                  ) : null}
                </div>
                {!readOnly && slotLocked[idx] && (
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() =>
                      unlinkSlot(idx, "Job unlinked. You can now select a new job.")
                    }
                  >
                    Change
                  </button>
                )}
                {!readOnly && slotLocked[idx] && (
                  <button
                    type="button"
                    className="danger-btn"
                    onClick={() => unlinkSlot(idx, "Job removed successfully!")}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </FormModal>

      <FormModal
        open={Boolean(confirmRemove.open)}
        title="Remove Table?"
        onCancel={closeConfirmRemove}
        footer={
          <>
            <button type="button" className="secondary-btn" onClick={closeConfirmRemove}>
              Cancel
            </button>
            <button type="button" className="danger-btn" onClick={confirmRemoveTable}>
              Yes, remove
            </button>
          </>
        }
      >
        <div style={{ fontSize: 14, color: "#334155" }}>
          This will permanently remove this table from the layout and delete its
          job assignments. Are you sure?
        </div>
      </FormModal>
    </>
  );
}
